// @ts-strict-ignore
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { computeCompletedChecklistItemsCount } from '../helpers';
import { RootState } from '../reducers/reducers';
import {
    FeatureSection,
    Nullable,
    ReleaseComponentProps,
    ReleaseStepComponentProps,
    StepRunComponentProps,
} from '../typescriptHelpers';
import CreateUpdateChecklistItemModal from './CreateEditChecklistItemModal';
import { StepChecklistItems } from './StepChecklistItems';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import Layout from 'antd/lib/layout';
import { antGrey, runwayColors } from './styles/globalStyles';
import Progress from 'antd/lib/progress';
import Text from 'antd/lib/typography/Text';
import {
    AppNotificationType,
    ChecklistItem,
    ChecklistItemPlacement,
    ChecklistItemStatusType,
    IntegrationCapability,
    ReleaseStepType,
    RunwayResponseType,
    SubscriptionFeatureType,
} from '../types';
import AddCommentComponent from './checklistItems/AddCommentComponent';
import {
    selectAppHasSelectCapabilitiesConfigured,
    selectCommonNotificationFromAppNotificationId,
    selectIntegrationForCapability,
    selectIsDemoModeEnabledForRelease,
    selectIsReleaseCompleted,
    selectJoinedCommsProvidersNamesList,
    selectOrgGroups,
    selectReleaseDisplayableVersion,
    selectReleaseStepsByReleaseType,
    selectReleaseType,
    selectReleaseVersion,
} from '../selectors/selectors';
import ApprovalsSortComponent from './ApprovalsSortComponent';
import ApprovalsSearchComponent from './ApprovalsSearchComponent';
import * as API from '../api';
import IntegrationIcon from './icons/IntegrationIcon';
import Tooltip from 'antd/lib/tooltip';
import Divider from 'antd/lib/divider';
import { globalMessage } from '../utils/globalMessage';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import { FilterableKeysAndOptions, FilterTableButton } from './tableFilters/FilterTableButton';
import { displayTextForChecklistItemStatusType, displayTextForUserGroup } from '../helpers/displayHelpers';
import { EmptyStateWrapper } from './releaseEmptyState/EmptyStateWrapper';
import { tryUpdateAppSettingsV2, UpdateChecklistItemsOrderRequest } from '../actions/appSettings';
import { AppSettingsV2Updatable } from '../actions/updatable';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepConfigUpdatable } from '@/actions/stepConfig';
import { StepRunUpdatable } from '@/actions/stepRun';

interface StepChecklistItemsContainerProps extends ReleaseStepComponentProps {
    items: ChecklistItem[];
    placement: ChecklistItemPlacement;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

export interface PingAllChecklistItemOwnersRequest extends ReleaseComponentProps {
    placement: ChecklistItemPlacement;
    releaseStep: ReleaseStepType;
}

export interface ChecklistFilterFormattingData {
    displayNameForUserId: Record<string, string>;
}

const StepChecklistContainer: React.FC<StepChecklistItemsContainerProps> = (
    props: StepChecklistItemsContainerProps,
) => {
    const { orgId, appId, releaseId, releaseStepType, placement } = props;
    const dispatch = useDispatch();
    const reminderNotificationTypeByPlacement: Record<ChecklistItemPlacement, AppNotificationType> = {
        [ChecklistItemPlacement.StepChecklist]: AppNotificationType.ChecklistItemsReminder,
        [ChecklistItemPlacement.Approvals]: AppNotificationType.ApprovalItemsReminder,
        [ChecklistItemPlacement.RegressionTesting]: AppNotificationType.RegressionItemsReminder,
    };
    const reminderDisabledMessageByPlacement: Record<ChecklistItemPlacement, string> = {
        [ChecklistItemPlacement.StepChecklist]: 'Checklist item reminder notification is not enabled',
        [ChecklistItemPlacement.Approvals]: 'Approval item reminder notification is not enabled',
        [ChecklistItemPlacement.RegressionTesting]: 'Regression item reminder notification is not enabled',
    };
    const reminderNotificationType = reminderNotificationTypeByPlacement[placement];
    const reminderDisabledMessage = reminderDisabledMessageByPlacement[placement];

    const [isPingAllChecklistItemOwnersLoading, setIsPingAllChecklistItemOwnersLoading] = React.useState(false);

    const [filteredItems, setFilteredItems] = React.useState<ChecklistItem[]>(props.items);
    const [reorderedItemsCount, setReorderedItemsCount] = React.useState(0);

    const totalItemsCount = filteredItems.length;
    const completedCount = computeCompletedChecklistItemsCount(filteredItems);

    const releaseVersion = useSelector((state: RootState) => selectReleaseVersion(state, props.releaseId));
    const displayableReleaseVersion = useSelector((state: RootState) =>
        selectReleaseDisplayableVersion(state, props.releaseId),
    );
    const isReleaseCompleted = useSelector((state: RootState) => selectIsReleaseCompleted(state, props.releaseId));

    const commsIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.Notifications),
    );

    const reminderNotificationSettings = useSelector((state: RootState) =>
        selectCommonNotificationFromAppNotificationId(state, appId, reminderNotificationType),
    );
    const hasCommsIntegrationConnected = useSelector((state: RootState) =>
        selectAppHasSelectCapabilitiesConfigured(state, appId, [IntegrationCapability.Notifications]),
    );

    const stepsByReleaseType = useSelector((state: RootState) => selectReleaseStepsByReleaseType(state, props.appId));
    const releaseType = useSelector((state: RootState) => selectReleaseType(state, props.releaseId));

    const [showAddChecklistItem, setShowAddChecklistItem] = React.useState(false);
    const isApprovalsList = placement === ChecklistItemPlacement.Approvals;
    const [isEditing, setIsEditing] = React.useState(false); // show edit/delete buttons
    const [itemIsEditing, setItemIsEditing] = React.useState<string>(null); // show edit item form for item with id
    const [commentingOnItem, setCommentingOnItem] = React.useState<string>(null);
    const editDisabled = !isEditing && totalItemsCount === 0;
    const completedPercent = (completedCount / totalItemsCount) * 100;

    const updateFilteredItems = (items) => {
        setFilteredItems(items);
        setReorderedItemsCount(reorderedItemsCount + 1);
    };

    const isDemoModeEnabled = useSelector((state: RootState) =>
        selectIsDemoModeEnabledForRelease(state, props.releaseId),
    );

    const [
        updateChecklistItemOrder,
        updateChecklistItemOrderIsLoading,
        updateChecklistItemOrderResponseType,
        updateChecklistItemOrderError,
    ] = useCallUpdatable(StepConfigUpdatable.UpdateChecklistItemTemplateOrder, props.stepRunComponentProps);

    const releaseStepItemOrderChangedSuccess = useUpdated(AppSettingsV2Updatable.UpdateReleaseStepChecklistItemOrder);
    const releaseStepItemOrderChangedIsLoading = useIsLoading(
        AppSettingsV2Updatable.UpdateReleaseStepChecklistItemOrder,
    );
    const releaseStepItemOrderChangedError = useError(AppSettingsV2Updatable.UpdateReleaseStepChecklistItemOrder);

    const itemOrderChangedSuccess =
        props.stepRunComponentProps ?
            updateChecklistItemOrderResponseType === RunwayResponseType.Success
        :   releaseStepItemOrderChangedSuccess;
    const itemOrderChangedIsLoading =
        props.stepRunComponentProps ? updateChecklistItemOrderIsLoading : releaseStepItemOrderChangedIsLoading;
    const itemOrderChangedError =
        props.stepRunComponentProps ? updateChecklistItemOrderError : releaseStepItemOrderChangedError;

    React.useEffect(() => {
        if (itemOrderChangedSuccess) {
            globalMessage.success('Checklist items order updated!');
            const updatedIsEditing = !isEditing;
            setIsEditing(updatedIsEditing);
            setReorderedItemsCount(0);
        }
    }, [itemOrderChangedSuccess]);

    React.useEffect(() => {
        if (itemOrderChangedError) {
            globalMessage.error(itemOrderChangedError);
        }
    }, [itemOrderChangedError]);

    const onEditItemsClicked = () => {
        if (isEditing && reorderedItemsCount > 0) {
            // Legacy and workflow endpoints expect different IDs
            const request: Omit<UpdateChecklistItemsOrderRequest, 'orderedItemIds'> = {
                orgId: orgId,
                appId: appId,
                releaseId: releaseId,
            };

            if (props.stepRunComponentProps) {
                updateChecklistItemOrder({
                    ...request,
                    originStepRunId: props.stepRunComponentProps.stepRunId,
                    orderedItemIds: filteredItems.map((item) => item.templateId),
                });
            } else {
                dispatch(
                    tryUpdateAppSettingsV2(AppSettingsV2Updatable.UpdateReleaseStepChecklistItemOrder, {
                        ...request,
                        orderedItemIds: filteredItems.map((item) => item.id),
                    }),
                );
            }
        } else {
            const updatedIsEditing = !isEditing;
            setIsEditing(updatedIsEditing);
        }
    };

    const hideCreateEditModal = () => {
        setItemIsEditing(null);
        setShowAddChecklistItem(false);
    };

    const auth = useSelector((state: RootState) => state.auth);
    const authCredentials = auth.authCredentials;

    const [
        pingAllChecklistItemsOwners,
        pingAllChecklistItemsOwnersIsLoading,
        pingAllChecklistItemsOwnersResponseType,
        pingAllChecklistItemsOwnersError,
    ] = useCallUpdatable(StepRunUpdatable.PingPendingChecklistItems, props.stepRunComponentProps);

    React.useEffect(() => {
        if (pingAllChecklistItemsOwnersResponseType === RunwayResponseType.Success) {
            globalMessage.success('All checklist item owners pinged!');
        }
    }, [pingAllChecklistItemsOwnersResponseType]);

    React.useEffect(() => {
        if (pingAllChecklistItemsOwnersError) {
            globalMessage.error(pingAllChecklistItemsOwnersError);
        }
    }, [pingAllChecklistItemsOwnersError]);

    const pingAllCliOwners = () => {
        const requestData: PingAllChecklistItemOwnersRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            placement: props.placement,
            releaseStep: props.releaseStepType,
        };
        const isReminderEnabled = reminderNotificationSettings?.enabled ?? false;
        if (!isReminderEnabled) {
            globalMessage.error(reminderDisabledMessage);
            return;
        }

        if (props.stepRunComponentProps) {
            pingAllChecklistItemsOwners();
            return;
        }

        setIsPingAllChecklistItemOwnersLoading(true);

        API.pingAllChecklistItemOwners(requestData, authCredentials)
            .then(() => {
                globalMessage.success('All checklist item owners pinged!');
                return;
            })
            .catch((e) => {
                globalMessage.error(e.message);
                return;
            })
            .finally(() => setIsPingAllChecklistItemOwnersLoading(false));
    };

    const disablePingAllPending =
        totalItemsCount === 0 || completedCount === totalItemsCount || !hasCommsIntegrationConnected;

    const commsProviders = useSelector(selectJoinedCommsProvidersNamesList);

    const renderPingAllButton = () => {
        const button = (
            <Button
                icon={<IntegrationIcon integrationId={commsIntegration?.id} />}
                disabled={disablePingAllPending}
                onClick={pingAllCliOwners}
                loading={isPingAllChecklistItemOwnersLoading || pingAllChecklistItemsOwnersIsLoading}
            >
                <span style={{ paddingLeft: 4 }}>Ping all pending</span>
            </Button>
        );

        if (!hasCommsIntegrationConnected) {
            return (
                <Tooltip title={`Connect ${commsProviders} to ping owners about incomplete items`}>{button}</Tooltip>
            );
        }

        return button;
    };

    const showForm = showAddChecklistItem || itemIsEditing !== null;
    const showAddEditButtons = !isReleaseCompleted || releaseStepType === ReleaseStepType.Rollout;
    const isCommentingOnItem = commentingOnItem ? filteredItems.find((item) => item.id === commentingOnItem) : null;
    const showProgress =
        props.placement === ChecklistItemPlacement.StepChecklist ||
        props.placement === ChecklistItemPlacement.RegressionTesting;

    const userGroups = useSelector((state: RootState) => selectOrgGroups(state, orgId));

    const filterableKeysAndOptions = React.useMemo(() => {
        const displayNameForGroupId = Object.values(userGroups).reduce(
            (acc, group) => ({
                ...acc,
                [group.id]: displayTextForUserGroup(group),
            }),
            {},
        );

        const options: FilterableKeysAndOptions<ChecklistItem> = {
            owners: {
                displayName: 'Owners',
                displayNameForPossibleValue: (value) => {
                    return value;
                },
            },
            approverGroups: {
                displayName: 'Approver user groups',
                extractPossibleValueOrValuesFromItem: (item) => item.approverGroups || [],
                displayNameForPossibleValue: (value) => displayNameForGroupId[value] || value,
                defaultValues: Object.keys(displayNameForGroupId),
                showAsDropdown: true,
            },
            status: {
                displayName: 'Status',
                displayNameForPossibleValue: (value) => {
                    return displayTextForChecklistItemStatusType(value as ChecklistItemStatusType);
                },
                defaultValues: Object.values(ChecklistItemStatusType),
                extractPossibleValueOrValuesFromItem: (item) => {
                    return item.status?.status ?? 'Not started';
                },
                showAsDropdown: true,
            },
        };
        return options;
    }, []);

    return (
        <Layout style={{ paddingBottom: 95 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '25px', marginTop: 24, marginBottom: 24 }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '20px',
                        alignItems: 'center',
                    }}
                >
                    {showProgress && (
                        <Progress
                            percent={Math.round(completedPercent)}
                            strokeColor={runwayColors.statusGreen}
                            type="line"
                            format={(percent) => {
                                return (
                                    <Text type="secondary">
                                        {percent}% complete ({completedCount} / {totalItemsCount})
                                    </Text>
                                );
                            }}
                            style={{ maxWidth: 200 }}
                        />
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 20,
                            justifyContent: isApprovalsList ? 'space-between' : 'flex-start',
                            width: isApprovalsList ? '100%' : 'auto',
                        }}
                    >
                        <Space size={8}>
                            {isApprovalsList && (
                                <>
                                    <ApprovalsSearchComponent />
                                    <ApprovalsSortComponent />
                                </>
                            )}
                            <FilterTableButton<ChecklistItem>
                                id={placement}
                                items={props.items}
                                filterableKeysAndOptions={filterableKeysAndOptions}
                                onChange={(filteredItems) => {
                                    setFilteredItems(filteredItems);
                                }}
                            />
                        </Space>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {renderPingAllButton()}
                            {showAddEditButtons && (
                                <>
                                    <Divider type="vertical" style={{ height: 'auto' }} />
                                    <Space>
                                        <PlanRestrictedFeatureWrapper
                                            feature={SubscriptionFeatureType.FeatureCLI}
                                            requiresTooltip
                                        >
                                            <Button
                                                onClick={() => {
                                                    setShowAddChecklistItem(true);
                                                }}
                                                icon={<PlusOutlined style={{ color: antGrey.grey7 }} />}
                                                disabled={isDemoModeEnabled}
                                            >
                                                Add
                                            </Button>
                                        </PlanRestrictedFeatureWrapper>
                                        <Button
                                            onClick={() => {
                                                onEditItemsClicked();
                                            }}
                                            disabled={editDisabled || isDemoModeEnabled}
                                            icon={isEditing ? null : <EditOutlined style={{ color: antGrey.grey7 }} />}
                                            loading={itemOrderChangedIsLoading}
                                        >
                                            {isEditing ? 'Done' : 'Edit'}
                                        </Button>
                                    </Space>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <EmptyStateWrapper
                releaseStepType={props.releaseStepType}
                appId={props.appId}
                featureSections={
                    placement === ChecklistItemPlacement.RegressionTesting ? [FeatureSection.RegressionTestingTestItems]
                    : placement === ChecklistItemPlacement.Approvals ?
                        [FeatureSection.ApprovalsItemList]
                    :   [FeatureSection.ChecklistItems]
                }
                releaseId={props.releaseId}
                shouldShowAsTooltip
                shouldForceDisable={props.items.length > 0}
            >
                <div>
                    <StepChecklistItems
                        orgId={orgId}
                        releaseId={releaseId}
                        appId={appId}
                        releaseStepType={releaseStepType}
                        items={filteredItems}
                        isEditing={isEditing}
                        onEditChecklistItem={(id) => setItemIsEditing(id)}
                        onAddComment={(id) => setCommentingOnItem(id)}
                        placement={placement}
                        onUpdateOrder={updateFilteredItems}
                        stepRunComponentProps={props.stepRunComponentProps}
                    />
                </div>
            </EmptyStateWrapper>
            <CreateUpdateChecklistItemModal
                orgId={orgId}
                appId={appId}
                open={showForm}
                onCancel={hideCreateEditModal}
                onSuccess={hideCreateEditModal}
                releaseStepTypes={stepsByReleaseType[releaseType] ?? []}
                isEditingItemWithId={itemIsEditing}
                defaultReleaseStepTypeOrStepConfigId={props.stepRunComponentProps?.stepConfigId ?? releaseStepType}
                placement={placement}
                releaseVersion={releaseVersion}
                displayableReleaseVersion={displayableReleaseVersion}
                stepRunComponentProps={props.stepRunComponentProps}
                releaseId={props.releaseId}
                releaseStepType={releaseStepType}
                isOrgLevelTemplate={false}
            />
            <AddCommentComponent
                stepRunComponentProps={props.stepRunComponentProps}
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId}
                item={isCommentingOnItem}
                open={!!isCommentingOnItem}
                onCancel={() => setCommentingOnItem(null)}
            />
        </Layout>
    );
};

export default StepChecklistContainer;
